/**
 * 2026.2 — RATES (FY2026-27 — 1 Jul 2026 → 30 Jun 2027).
 *
 * Bump reason: FY27 rolled over on 1 Jul 2026. Cents-per-km rises from
 * 88c (FY26, ruleset 2026.1) to 91c (FY27, this ruleset) per PCG /
 * Determination 2026. WFH fixed rate stays at 70c/hour until the ATO
 * publishes a new FY27 figure. IAWO stays at $20,000 (see thresholds).
 *
 * Numbers below are sourced from ato.gov.au (see `source` per rate and
 * the `references.ts` block for the full PCG list). This file is FROZEN
 * once merged with `status: "confirmed"` — a future rate change means a
 * new ruleset version, never an edit in place.
 */

import type { AtoRates } from "../types";

export const rates: AtoRates = {
  status: "confirmed",

  /**
   * GST rate — unchanged since 1 Jul 2000. See 2026.1/rates.ts for the
   * consumer-facing math (1/11 rule).
   */
  gst: {
    amount: 10,
    unit: "percent",
    source:
      "ATO — GST (rate 10% since 1 Jul 2000): https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst",
  },

  /**
   * Working-from-home FIXED-RATE method — dollars per hour. Held at
   * 70c/hour for FY27 pending ATO update. If the ATO publishes a new
   * FY27 figure mid-year, ship 2026.3 with the new value.
   */
  wfhFixedRate: {
    amount: 0.70,
    unit: "AUD/hour",
    source:
      "ATO Fixed Rate Method + PCG 2023/1: https://www.ato.gov.au/individuals-and-families/income-deductions-offsets-and-records/deductions-you-can-claim/work-related-deductions/working-from-home-expenses/fixed-rate-method",
  },

  /**
   * Cents-per-kilometre method — 91c/km for FY27 (up from 88c in FY26).
   * Max 5,000 business km per car per income year. Covers ALL car
   * running costs (fuel + rego + insurance + maintenance + depreciation)
   * so no separate depreciation claim allowed under this method.
   *
   * Applies from 1 Jul 2026 (start of FY27). Historic entries stamped
   * with ruleset 2026.1 continue to compute at 88c/km forever.
   */
  centsPerKm: {
    amount: 0.91,
    unit: "AUD/km",
    source:
      "ATO — Cents-per-kilometre method (91c/km for FY26-27; 88c/km for FY25-26): https://www.ato.gov.au/individuals-and-families/income-deductions-offsets-and-records/deductions-you-can-claim/work-related-deductions/cars-transport-and-travel/motor-vehicle-and-car-expenses/expenses-for-a-car-you-own-or-lease/cents-per-kilometre-method",
  },

  /**
   * Concessional super contributions cap — rises to $32,500 for FY27
   * (from $30,000 in FY26). Combined limit for employer SG + salary
   * sacrifice + personal deductible contributions per member per FY.
   */
  superConcessionalCap: {
    amount: 32500,
    unit: "AUD/year",
    source:
      "ATO — Concessional contributions cap: https://www.ato.gov.au/individuals-and-families/super-for-individuals-and-families/super/growing-and-keeping-track-of-your-super/caps-limits-and-tax-on-super-contributions/concessional-contributions-cap",
  },
};
